import * as React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import MoveToInboxOutlinedIcon from '@mui/icons-material/MoveToInboxOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined';

import dayjs from 'dayjs';

import useAxios from '../../utils/useAxios';
import { SectionCard, EmptyState, ConfirmDialog, currency, number } from '../ui';
import { TRUE_COLORS } from '../../theme';

const STATUS = {
  pending: { label: 'Pendiente', bg: 'rgba(183,121,31,0.12)', color: TRUE_COLORS.warning },
  received: { label: 'Recibida', bg: 'rgba(46,125,91,0.12)', color: TRUE_COLORS.success },
  cancelled: { label: 'Cancelada', bg: 'rgba(0,0,0,0.06)', color: TRUE_COLORS.secondary },
} as const;

const OrderStatusChip = ({ status }: { status: keyof typeof STATUS }) => {
  const s = STATUS[status] ?? STATUS.pending;
  return <Chip size="small" label={s.label} sx={{ bgcolor: s.bg, color: s.color, fontWeight: 600 }} />;
};

type Line = { product: any; quantity: string; price: string };

const NewOrderDialog = ({ open, onClose, onCreated, suppliers, locations }: {
  open: boolean; onClose: () => void; onCreated: () => void; suppliers: any[]; locations: any[];
}) => {
  const axios = useAxios();
  const [supplier, setSupplier] = React.useState('');
  const [destination, setDestination] = React.useState('');
  const [products, setProducts] = React.useState<any[]>([]);
  const [lines, setLines] = React.useState<Line[]>([]);
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (open) { setSupplier(''); setDestination(locations[0]?.id ? String(locations[0].id) : ''); setLines([]); setError(''); }
  }, [open, locations]);

  React.useEffect(() => {
    if (!open) return;
    axios.get('api/products/').then(r => setProducts(r.data ?? [])).catch(() => {});
  }, [axios, open]);

  // Suggest the supplier's products first, priced at that supplier's cost when known.
  const options = React.useMemo(() => {
    const sid = Number(supplier);
    return [...products].sort((a, b) => {
      const as = (a.suppliers ?? []).some((s: any) => s.id === sid) ? 0 : 1;
      const bs = (b.suppliers ?? []).some((s: any) => s.id === sid) ? 0 : 1;
      return as - bs || a.name.localeCompare(b.name);
    });
  }, [products, supplier]);

  const costFor = (p: any) => {
    const ssc = (p.supplier_specific_costs ?? []).find((c: any) => c.supplier?.id === Number(supplier));
    return String(ssc?.cost ?? p.cost ?? 0);
  };

  const total = lines.reduce((acc, l) => acc + (Number(l.quantity) || 0) * (Number(l.price) || 0), 0);

  const create = async () => {
    if (!destination) { setError('Elige la sucursal que recibirá la mercancía.'); return; }
    if (lines.length === 0) { setError('Agrega al menos un producto.'); return; }
    setSaving(true);
    try {
      await axios.post('inventory/orders/', {
        supplier: supplier ? Number(supplier) : null,
        destination: Number(destination),
        items: lines.map(l => ({ product: l.product.id, quantity: Number(l.quantity) || 0, price: Number(l.price) || 0 })),
        amount: Math.round(total * 100) / 100,
      });
      onCreated();
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(typeof data === 'object' && data ? Object.values(data).flat().join(' ') : 'No se pudo crear la orden.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Nueva orden de compra</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth label="Proveedor" value={supplier} onChange={e => setSupplier(e.target.value)}>
                <MenuItem value="">Sin proveedor</MenuItem>
                {suppliers.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField select fullWidth required label="Recibe en" value={destination} onChange={e => setDestination(e.target.value)}>
                {locations.map(l => <MenuItem key={l.id} value={String(l.id)}>{l.name}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>

          <Autocomplete
            options={options}
            value={null}
            blurOnSelect
            clearOnBlur
            getOptionLabel={o => `${o.name} · ${o.barcode}`}
            groupBy={o => ((o.suppliers ?? []).some((s: any) => s.id === Number(supplier)) ? 'De este proveedor' : 'Otros productos')}
            onChange={(_, p) => {
              if (!p) return;
              setLines(ls => ls.some(l => l.product.id === p.id)
                ? ls.map(l => (l.product.id === p.id ? { ...l, quantity: String((Number(l.quantity) || 0) + 1) } : l))
                : [...ls, { product: p, quantity: '1', price: costFor(p) }]);
            }}
            renderInput={params => <TextField {...params} label="Agregar producto" placeholder="Busca por nombre o código" />}
            noOptionsText="Sin productos"
          />

          {lines.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell sx={{ width: 120 }}>Cantidad</TableCell>
                  <TableCell sx={{ width: 140 }}>Costo unitario</TableCell>
                  <TableCell align="right" sx={{ width: 120 }}>Subtotal</TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l, i) => (
                  <TableRow key={l.product.id}>
                    <TableCell sx={{ fontWeight: 500 }}>{l.product.name}</TableCell>
                    <TableCell>
                      <TextField type="number" value={l.quantity} onChange={e => setLines(ls => ls.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} slotProps={{ htmlInput: { min: 0, step: 'any' } }} />
                    </TableCell>
                    <TableCell>
                      <TextField type="number" value={l.price} onChange={e => setLines(ls => ls.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} slotProps={{ htmlInput: { min: 0, step: '0.01' } }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{currency((Number(l.quantity) || 0) * (Number(l.price) || 0))}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'baseline' }} spacing={1.5}>
            <Typography sx={{ color: 'text.secondary' }}>Total</Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem' }}>{currency(total)}</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={create} disabled={saving}>Crear orden</Button>
      </DialogActions>
    </Dialog>
  );
};

const OrdersTab = ({ locations }: { locations: any[] }) => {
  const axios = useAxios();
  const [orders, setOrders] = React.useState<any[]>([]);
  const [suppliers, setSuppliers] = React.useState<any[]>([]);
  const [productNames, setProductNames] = React.useState<Record<number, string>>({});
  const [loading, setLoading] = React.useState(true);
  const [refresh, setRefresh] = React.useState(0);
  const [creating, setCreating] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [confirm, setConfirm] = React.useState<null | { action: 'receive' | 'cancel' | 'pay' | 'mail' }>(null);
  const [notice, setNotice] = React.useState('');

  React.useEffect(() => {
    (async () => {
      const [o, s, p, d] = await Promise.allSettled([
        axios.get('inventory/orders/'), axios.get('inventory/suppliers/'), axios.get('api/products/'), axios.get('api/deleted-products/'),
      ]);
      if (o.status === 'fulfilled') setOrders(o.value.data ?? []);
      if (s.status === 'fulfilled') setSuppliers(s.value.data ?? []);
      // Order items only carry the product id.
      const names: Record<number, string> = {};
      [p, d].forEach(r => { if (r.status === 'fulfilled') (r.value.data ?? []).forEach((prod: any) => { names[prod.id] = prod.name; }); });
      setProductNames(names);
      setLoading(false);
    })();
  }, [axios, refresh]);

  const selected = orders.find(o => o.id === selectedId) ?? null;

  const run = async (action: 'receive' | 'cancel' | 'pay' | 'mail') => {
    if (!selected) return;
    const url = { receive: 'receive', cancel: 'cancel', pay: 'pay', mail: 'mail' }[action];
    try {
      await axios.post(`inventory/orders/${url}/${selected.id}/`);
      setNotice({
        receive: 'Orden recibida. El inventario de la sucursal se actualizó.',
        cancel: 'Orden cancelada.',
        pay: 'Orden marcada como pagada.',
        mail: 'Enviamos la orden por correo al proveedor.',
      }[action]);
    } catch (err: any) {
      setNotice(typeof err?.response?.data === 'string' ? err.response.data : 'No se pudo completar la acción.');
    }
    setConfirm(null);
    setRefresh(r => r + 1);
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'Orden', width: 90, valueFormatter: v => `#${v}` },
    {
      field: 'date', headerName: 'Fecha', flex: 0.8, minWidth: 130, type: 'dateTime',
      valueGetter: v => (v ? new Date(v) : null), valueFormatter: (v: Date | null) => (v ? dayjs(v).format('D MMM YYYY') : '—'),
    },
    { field: 'supplier', headerName: 'Proveedor', flex: 1, minWidth: 160, valueGetter: (_v, row: any) => row.supplier?.name ?? 'Sin proveedor' },
    { field: 'destination', headerName: 'Recibe en', flex: 0.9, minWidth: 140, valueGetter: (_v, row: any) => row.destination?.name ?? '—' },
    { field: 'items', headerName: 'Artículos', type: 'number', flex: 0.5, minWidth: 100, valueGetter: (v: any[]) => (v ?? []).length },
    { field: 'amount', headerName: 'Total', type: 'number', flex: 0.6, minWidth: 120, valueFormatter: v => currency(v) },
    {
      field: 'status', headerName: 'Estado', flex: 0.7, minWidth: 150,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%' }}>
          <OrderStatusChip status={row.status} />
          {row.date_paid && <Chip size="small" variant="outlined" label="Pagada" />}
        </Stack>
      ),
    },
  ];

  return (
    <>
      {notice && <Alert severity="info" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}
      <SectionCard noPadding>
        {!loading && orders.length === 0 ? (
          <EmptyState
            icon={<ReceiptLongOutlinedIcon />}
            title="Sin órdenes de compra"
            description="Pide mercancía a tus proveedores. Al recibirla, el inventario se actualiza solo."
            action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={locations.length === 0}>Nueva orden</Button>}
          />
        ) : (
          <DataGrid
            rows={orders}
            columns={columns}
            loading={loading}
            autoHeight
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } }, sorting: { sortModel: [{ field: 'date', sort: 'desc' }] } }}
            onRowClick={({ row }) => setSelectedId(row.id)}
            slots={{
              toolbar: () => (
                <Stack direction="row" sx={{ px: 2, pt: 2, pb: 1, justifyContent: 'flex-end' }}>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>Nueva orden</Button>
                </Stack>
              ),
            }}
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        )}
      </SectionCard>

      <Drawer anchor="right" open={!!selected} onClose={() => setSelectedId(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
        {selected && (
          <Stack sx={{ height: '100%' }}>
            <Stack direction="row" sx={{ p: 3, pb: 2, justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="h5">Orden #{selected.id}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  {dayjs(selected.date).format('D [de] MMMM YYYY, HH:mm')}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
                  <OrderStatusChip status={selected.status} />
                  {selected.date_paid && <Chip size="small" variant="outlined" label={`Pagada ${dayjs(selected.date_paid).format('D MMM')}`} />}
                  {selected.email_sent && <Chip size="small" variant="outlined" label="Enviada por correo" />}
                </Stack>
              </Box>
              <IconButton onClick={() => setSelectedId(null)}><CloseIcon /></IconButton>
            </Stack>
            <Divider />
            <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={6}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Proveedor</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{selected.supplier?.name ?? 'Sin proveedor'}</Typography>
                  {selected.supplier?.email && <Typography variant="body2" sx={{ color: 'text.secondary' }}>{selected.supplier.email}</Typography>}
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>Recibe en</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{selected.destination?.name ?? '—'}</Typography>
                </Grid>
              </Grid>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="right">Cant.</TableCell>
                    <TableCell align="right">Costo</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(selected.items ?? []).map((item: any) => (
                    <TableRow key={item.id}>
                      <TableCell>{productNames[item.product] ?? 'Producto eliminado'}</TableCell>
                      <TableCell align="right">{number(item.quantity, 2)}</TableCell>
                      <TableCell align="right">{currency(item.price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{currency(item.quantity * item.price)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Stack direction="row" sx={{ justifyContent: 'flex-end', mt: 2 }} spacing={1.5}>
                <Typography sx={{ color: 'text.secondary' }}>Total</Typography>
                <Typography sx={{ fontWeight: 700 }}>{currency(selected.amount)}</Typography>
              </Stack>
            </Box>
            <Divider />
            <Stack spacing={1} sx={{ p: 3 }}>
              {selected.status === 'pending' && (
                <Button variant="contained" startIcon={<MoveToInboxOutlinedIcon />} onClick={() => setConfirm({ action: 'receive' })}>
                  Marcar como recibida
                </Button>
              )}
              <Stack direction="row" spacing={1}>
                {!selected.date_paid && selected.status !== 'cancelled' && (
                  <Button fullWidth variant="outlined" startIcon={<PaidOutlinedIcon />} onClick={() => setConfirm({ action: 'pay' })}>Marcar pagada</Button>
                )}
                {selected.supplier?.email && !selected.email_sent && selected.status === 'pending' && (
                  <Button fullWidth variant="outlined" startIcon={<ForwardToInboxOutlinedIcon />} onClick={() => setConfirm({ action: 'mail' })}>Enviar al proveedor</Button>
                )}
              </Stack>
              {selected.status === 'pending' && (
                <Button color="error" startIcon={<DoNotDisturbOnOutlinedIcon />} onClick={() => setConfirm({ action: 'cancel' })}>Cancelar orden</Button>
              )}
            </Stack>
          </Stack>
        )}
      </Drawer>

      <ConfirmDialog
        open={!!confirm}
        danger={confirm?.action === 'cancel'}
        title={{
          receive: '¿Marcar la orden como recibida?',
          cancel: '¿Cancelar esta orden?',
          pay: '¿Marcar la orden como pagada?',
          mail: '¿Enviar la orden al proveedor?',
        }[confirm?.action ?? 'receive']}
        description={{
          receive: `Se sumarán las cantidades al inventario de ${selected?.destination?.name ?? 'la sucursal'}.`,
          cancel: 'La orden quedará cancelada y no afectará tu inventario.',
          pay: 'Se registrará la fecha de pago de hoy.',
          mail: `Enviaremos el detalle a ${selected?.supplier?.email}.`,
        }[confirm?.action ?? 'receive']}
        confirmLabel={{ receive: 'Recibir', cancel: 'Cancelar orden', pay: 'Marcar pagada', mail: 'Enviar' }[confirm?.action ?? 'receive']}
        onClose={() => setConfirm(null)}
        onConfirm={() => run(confirm!.action)}
      />

      <NewOrderDialog open={creating} onClose={() => setCreating(false)} onCreated={() => setRefresh(r => r + 1)} suppliers={suppliers} locations={locations} />
    </>
  );
};

export default OrdersTab;
